import { Archive, MessageSquarePlus } from 'lucide-react';

import { Button } from '@accio3d/design-system/button';
import { cn } from '@accio3d/design-system/utils';
import { timeAgo } from '@accio3d/shared-ui/utils';

import { AgentAttentionPill } from './agent-pill';
import { useAgentConversations } from '../../hooks/useAgentConversations';
import { useAgentThread } from '../../hooks/useAgentSidebar';

const relativeTime = (iso: string | null): string => (iso ? timeAgo(iso) : '');

export function AgentConversationList({ onSelect }: { onSelect: () => void }) {
  const { threads, isLoading, archive } = useAgentConversations();
  const { threadId, setThreadId } = useAgentThread();

  const open = (id: string | undefined) => {
    setThreadId(id);
    onSelect();
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-2 border-b border-border/50 px-3 py-2">
        <span className="text-sm font-medium">Conversations</span>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-7 gap-1 text-xs"
          onClick={() => open(undefined)}
        >
          <MessageSquarePlus className="h-3.5 w-3.5" />
          New
        </Button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto p-2">
        {isLoading && <p className="px-1 text-sm text-muted-foreground">Loading…</p>}

        {!isLoading && threads.length === 0 && (
          <p className="px-1 text-sm text-muted-foreground">
            No conversations yet. Ask me something and it will appear here.
          </p>
        )}

        <ul className="space-y-1">
          {threads.map((thread) => (
            <li key={thread.id} className="group relative">
              <button
                type="button"
                onClick={() => open(thread.id)}
                aria-current={thread.id === threadId ? 'true' : undefined}
                className={cn(
                  'w-full rounded-md px-2 py-1.5 pr-8 text-left transition-colors',
                  'hover:bg-muted/60',
                  thread.id === threadId && 'bg-muted',
                )}
              >
                <span className="block truncate text-sm">{thread.title ?? 'New conversation'}</span>
                <span className="mt-0.5 flex items-center gap-1.5">
                  <span className="text-[11px] text-muted-foreground">
                    {relativeTime(thread.lastMessageAt ?? thread.createdAt)}
                  </span>
                  {thread.status === 'needs_input' && (
                    <AgentAttentionPill>Waiting on you</AgentAttentionPill>
                  )}
                </span>
              </button>

              <Button
                size="icon"
                variant="ghost"
                title="Archive"
                className="absolute right-1 top-1 h-6 w-6 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
                disabled={archive.isPending}
                onClick={() => {
                  archive.mutate(thread.id);
                  if (thread.id === threadId) setThreadId(undefined);
                }}
              >
                <Archive className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
